import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const Client = () => {
  const [tipo, setTipo] = useState("N");
  const [esCliente, setEsCliente] = useState(true);
  const [esProveedor, setEsProveedor] = useState(false);
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: "error", message: "" });

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      tipo,
      razon_social: formData.get("razon_social")?.toUpperCase().trim() || "",
      nombre_comercial: formData.get("nombre_comercial")?.toUpperCase().trim() || "",
      cedula: formData.get("cedula")?.trim() || "",
      ruc: formData.get("ruc")?.trim() || "",
      telefono: formData.get("telefono")?.trim() || "",
      correo: formData.get("correo")?.trim() || "",
      direccion: formData.get("direccion")?.trim() || "",
      es_cliente: esCliente,
      es_proveedor: esProveedor,
      es_empleado: false,
      es_extranjero: false,
    };

    try {
      const res = await fetch("/clientes/crear/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        e.target.reset();
        setTipo("N");
        setEsCliente(true);
        setEsProveedor(false);
        navigate("/admin-panel/clientes", {
          state: { snackbar: { type: "success", message: "Persona registrada correctamente." } },
          replace: true,
        });
      } else {
        let msg = "Error al guardar la persona.";
        try {
          const err = await res.json();
          if (err?.error) msg = err.error;
        } catch {}
        setSnackbar({ open: true, type: "error", message: msg });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "No se pudo conectar con el servidor." });
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
            >
              Regresar
            </Button>
          </Box>

          <Paper
            sx={{
              p: { xs: 2, sm: 3, md: 3.2 },
              maxWidth: 900,
              mx: "auto",
              mb: 6,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2.5,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Registrar persona
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Completa la información principal del cliente o proveedor
                </Typography>
              </Box>

            </Box>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Persona</InputLabel>
                    <Select value={tipo} label="Tipo de Persona" onChange={(e) => setTipo(e.target.value)}>
                      <MenuItem value="N">Natural</MenuItem>
                      <MenuItem value="J">Jurídica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      minHeight: 40,
                      display: "flex",
                      alignItems: "center",
                      px: 1.2,
                      border: "1px solid #dbe3f0",
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={esCliente}
                            onChange={(e) => setEsCliente(e.target.checked)}
                            sx={{ py: 0.4 }}
                          />
                        }
                        label="Cliente"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={esProveedor}
                            onChange={(e) => setEsProveedor(e.target.checked)}
                            sx={{ py: 0.4 }}
                          />
                        }
                        label="Proveedor"
                      />
                    </FormGroup>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField label="Cédula" name="cedula" fullWidth size="small" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField label="RUC" name="ruc" fullWidth size="small" />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={tipo === "N" ? "Nombre completo" : "Razón Social"}
                    name="razon_social"
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Nombre Comercial" name="nombre_comercial" fullWidth size="small" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField label="Teléfono" name="telefono" fullWidth size="small" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField label="Correo Electrónico" name="correo" type="email" fullWidth size="small" />
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Dirección" name="direccion" multiline rows={3} fullWidth size="small" />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{ mt: 3 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" },
                    borderRadius: 2,
                    px: 2.4,
                    fontWeight: 700,
                    boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                  }}
                >
                  Guardar Persona
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Client;